import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, HandHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHero } from "@/components/PageHero";
import { api, formatApiErrorDetail } from "@/lib/api";
import { IMG } from "@/lib/assets";

const ROLES = ["Youth Mentorship", "Workshop Facilitation", "Event Support", "Administrative Help", "Outreach & Community", "Fundraising"];

export default function Volunteer() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", availability: "", support_mode: "Houston", skills: "", message: "" });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggleRole = (r) => setRoles((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/volunteers", { ...form, roles });
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", availability: "", support_mode: "Houston", skills: "", message: "" });
      setRoles([]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <main>
      <PageHero overline="Volunteer With Us" image={IMG.community2}
        title="Give your time, change a life"
        subtitle="Join our community of volunteers supporting mentorship, workshops, and events across Houston and beyond." />

      <section className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-20">
        <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 p-8 shadow-xl space-y-6">
          <div className="grid sm:grid-cols-2 gap-5">
            <div><Label>Full Name *</Label><Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-2 h-12 rounded-xl" data-testid="volunteer-name" /></div>
            <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-2 h-12 rounded-xl" data-testid="volunteer-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-2 h-12 rounded-xl" data-testid="volunteer-phone" /></div>
            <div><Label>Support Mode</Label>
              <Select value={form.support_mode} onValueChange={(v) => set("support_mode", v)}>
                <SelectTrigger className="mt-2 h-12 rounded-xl" data-testid="volunteer-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Houston">In-Person (Houston)</SelectItem><SelectItem value="Remote">Remote</SelectItem><SelectItem value="Both">Both</SelectItem></SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Roles you're interested in</Label>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              {ROLES.map((r) => (
                <label key={r} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${roles.includes(r) ? "border-gold bg-gold/5" : "border-slate-200"}`}>
                  <Checkbox checked={roles.includes(r)} onCheckedChange={() => toggleRole(r)} data-testid={`volunteer-role-${r.split(" ")[0].toLowerCase()}`} />
                  <span className="text-sm text-navy-800">{r}</span>
                </label>
              ))}
            </div>
          </div>

          <div><Label>Availability</Label><Input value={form.availability} onChange={(e) => set("availability", e.target.value)} placeholder="e.g. Weekends, weekday evenings" className="mt-2 h-12 rounded-xl" data-testid="volunteer-availability" /></div>
          <div><Label>Relevant Skills</Label><Textarea value={form.skills} onChange={(e) => set("skills", e.target.value)} placeholder="Tell us about your background and skills" className="mt-2 rounded-xl" data-testid="volunteer-skills" /></div>
          <div><Label>Anything else?</Label><Textarea value={form.message} onChange={(e) => set("message", e.target.value)} className="mt-2 rounded-xl" data-testid="volunteer-message" /></div>

          <Button type="submit" disabled={loading} data-testid="volunteer-form-submit" className="w-full h-14 rounded-full bg-navy-900 hover:bg-navy-700 text-white font-bold text-base">
            {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Submitting...</> : <><HandHeart className="h-5 w-5 mr-2" /> Submit Application</>}
          </Button>
        </form>
      </section>
    </main>
  );
}
